import { Fragment, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api, API_URL, getToken } from '../lib/api';
import { useAuth, canChangeContainerStatus } from '../lib/auth';

/**
 * The Inspection section for ONE container — the same panel in IMS and on the
 * indent Fact Sheet, reading the same record (services/container-inspection.ts).
 *
 * Each inspection shows: date, who inspected, result, report, checklist,
 * certificate, supporting documents and photographs. Photos and reports open
 * inside the page; nobody is sent to Drive or the survey sheet.
 */

interface InspFile { label: string; fileId?: string; driveId?: string; kind?: 'image' | 'video' | 'pdf' | 'file' }
type ItemStatus = 'ok' | 'checked' | 'issue' | 'na' | 'pending';
interface Item { key: string; label: string; section: string; status: ItemStatus; remark: string; validUntil?: string | null; validityText?: string; docStatus?: string; files?: InspFile[] }
interface Rec {
  id: string; source: string; sourceLabel: string; stage: string; date: string | null; time: string; inspector: string; location: string;
  result: string; resultTone: 'good' | 'warn' | 'bad' | 'neutral'; remarks: string; indentId: string;
  report: InspFile | null; certificate: (InspFile & { validUntil?: string | null }) | null;
  supportingDocs: InspFile[]; photos: InspFile[]; readings: Array<{ label: string; value: string }>;
  checklist: Item[]; issues: string[]; signOffs: Array<{ name: string; status: string; comment: string; at: string }>; note: string; manualId?: string;
}
interface View {
  containerNo: string; assetKind: string; assetLabel: string;
  template: { title: string; sections: Array<{ title: string; items: Array<{ key: string; label: string }> }> };
  records: Rec[];
  summary: { total: number; latestDate: string | null; latestResult: string; openIssues: number; photos: number; hasReport: boolean; surveyed: boolean };
  sources: { sheetError?: string };
}

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function fmt(iso?: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return y ? `${String(d).padStart(2, '0')}-${MON[m - 1]}-${y}` : '—';
}
const TONE: Record<string, string> = {
  good: 'border-emerald-200 bg-emerald-50 text-emerald-800', warn: 'border-amber-200 bg-amber-50 text-amber-800',
  bad: 'border-rose-200 bg-rose-50 text-rose-700', neutral: 'border-slate-200 bg-slate-50 text-slate-600',
};
const STATUS: Record<ItemStatus, { text: string; cls: string }> = {
  ok: { text: '✓ OK', cls: 'text-emerald-700' }, checked: { text: '✓ Photographed', cls: 'text-sky-700' },
  issue: { text: '✗ Issue', cls: 'font-semibold text-rose-700' }, na: { text: 'N/A', cls: 'text-slate-400' }, pending: { text: '—', cls: 'text-slate-300' },
};
const DOC_BADGE: Record<string, string> = {
  expired: 'border-rose-200 bg-rose-50 text-rose-700', expiring: 'border-amber-200 bg-amber-50 text-amber-800',
  valid: 'border-emerald-200 bg-emerald-50 text-emerald-700', no_date: 'border-slate-200 bg-slate-50 text-slate-600',
  no_expiry: 'border-slate-200 bg-slate-50 text-slate-500', missing: 'border-rose-200 border-dashed text-rose-600',
};

/** Fetch an authenticated file as a same-origin blob URL (an <img>/<iframe> cannot send the Bearer header). */
function useBlobUrl(path?: string): { src: string; failed: boolean } {
  const [src, setSrc] = useState('');
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!path) return;
    let cancelled = false; let made = '';
    setSrc(''); setFailed(false);
    const token = getToken();
    fetch(`${API_URL}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: 'include' })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(String(r.status)))))
      .then((b) => { if (cancelled) return; made = URL.createObjectURL(b); setSrc(made); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; if (made) URL.revokeObjectURL(made); };
  }, [path]);
  return { src, failed };
}

type Media = { path: string; label: string; kind?: InspFile['kind'] };

function Thumb({ m, onClick }: { m: Media; onClick: () => void }) {
  const { src, failed } = useBlobUrl(m.path);
  return (
    <figure className="m-0 w-[104px]">
      {src
        ? <img src={src} alt={m.label} loading="lazy" onClick={onClick} className="h-[78px] w-[104px] cursor-zoom-in rounded border border-slate-300 object-cover" />
        : <div className="flex h-[78px] w-[104px] items-center justify-center rounded border border-slate-200 bg-slate-100 text-[10px] text-slate-400">{failed ? 'unavailable' : '…'}</div>}
      <figcaption className="mt-0.5 truncate text-[10px] text-slate-600" title={m.label}>{m.label}</figcaption>
    </figure>
  );
}

function Viewer({ m, onClose }: { m: Media; onClose: () => void }) {
  const { src, failed } = useBlobUrl(m.path);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);
  const isImg = m.kind === 'image';
  return createPortal(
    <div className="fixed inset-0 z-[80] flex flex-col items-center justify-center bg-black/80 p-4" onClick={onClose}>
      <div className="mb-2 text-sm font-semibold text-white">{m.label} <span className="ml-2 text-xs font-normal text-white/60">click outside or press Esc to close</span></div>
      <div onClick={(e) => e.stopPropagation()}>
        {!src ? <div className="max-w-md text-center text-white/80">{failed ? (m.kind === 'pdf' ? 'This report is stored in Google Drive and has not been shared with the system yet, so it cannot be shown here. Ask the administrator to share the inspection PDFs with the system’s service account.' : 'Could not load the file') : 'Loading…'}</div>
          : isImg ? <img src={src} alt={m.label} className="max-h-[85vh] max-w-[92vw] object-contain" />
          : <iframe src={src} title={m.label} className="h-[85vh] w-[min(92vw,1000px)] border border-slate-300 bg-white" />}
      </div>
    </div>,
    document.body,
  );
}

export interface InspectionPanelProps {
  containerNo: string;
  /** API prefix for this container, e.g. `/api/ims-containers/containers/ABCU1234567` or the indent route. */
  base: string;
  /** Lets the viewer file an inspection (IMS only). */
  allowFiling?: boolean;
  /** Start with the newest record open (default) or everything closed. */
  compact?: boolean;
}

export default function ContainerInspectionPanel({ containerNo, base, allowFiling = false, compact = false }: InspectionPanelProps) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canFile = allowFiling && canChangeContainerStatus(user as any);
  const key = ['container-inspection', base];
  const { data, isLoading, error } = useQuery<View>({ queryKey: key, queryFn: () => api.get<View>(`${base}/inspection`), staleTime: 2 * 60_000 });
  const [openId, setOpenId] = useState<string | null>(null);
  const [view, setView] = useState<Media | null>(null);
  const [filing, setFiling] = useState(false);

  if (isLoading) return <div className="text-xs text-slate-400">Reading the inspection records…</div>;
  if (error || !data) return <div className="text-xs text-rose-600">{(error as any)?.message || 'Could not load the inspection details.'}</div>;

  const media = (f: InspFile, size: 'thumb' | 'full' = 'full'): Media | null => {
    if (f.fileId) return { path: `/api/files/${f.fileId}`, label: f.label, kind: f.kind };
    if (f.driveId) return { path: `${base}/inspection-file?driveId=${encodeURIComponent(f.driveId)}${size === 'thumb' ? '&size=thumb' : ''}`, label: f.label, kind: f.kind };
    return null;
  };
  const fileBtn = (f: InspFile, text?: string) => {
    const m = media(f);
    if (!m) return null;
    return <button key={`${f.fileId || f.driveId}-${f.label}`} type="button" onClick={() => setView({ ...m, kind: f.kind === 'image' ? 'image' : f.kind === 'pdf' ? 'pdf' : undefined })} className="rounded border border-slate-300 bg-white px-1.5 py-0.5 text-[11px] font-semibold text-[#1b3a57] hover:bg-slate-100" title={f.label}>{text || f.label}</button>;
  };

  const s = data.summary;
  const first = data.records[0]?.id;
  const shown = (r: Rec) => (openId === null ? (!compact && r.id === first) : openId === r.id);

  return (
    <div className="space-y-3 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded border border-slate-200 bg-slate-50 px-2 py-0.5 font-semibold text-slate-700">{data.assetLabel}</span>
        {s.total === 0
          ? <span className="text-slate-500">No inspection is on record for this container yet.</span>
          : <>
              <span className="text-slate-600">{s.total} inspection{s.total === 1 ? '' : 's'} on record</span>
              {s.latestDate && <span className="text-slate-600">· latest {fmt(s.latestDate)}{s.latestResult ? ` — ${s.latestResult}` : ''}</span>}
              {s.openIssues > 0 && <span className="rounded border border-amber-200 bg-amber-50 px-2 py-0.5 font-semibold text-amber-800">{s.openIssues} point{s.openIssues === 1 ? '' : 's'} to attend</span>}
              {s.photos > 0 && <span className="text-slate-500">· {s.photos} photograph{s.photos === 1 ? '' : 's'}</span>}
            </>}
        {canFile && <button type="button" onClick={() => setFiling((v) => !v)} className="ml-auto rounded-md border border-indigo-300 px-2 py-1 font-semibold text-indigo-700 hover:bg-indigo-50">{filing ? 'Cancel' : '+ File an inspection'}</button>}
      </div>
      {data.sources.sheetError && <div className="text-amber-700">The surveyor’s sheet could not be read just now — survey inspections may be missing. ({data.sources.sheetError})</div>}

      {filing && canFile && <FilingForm containerNo={containerNo} base={base} view={data} onDone={() => { setFiling(false); qc.invalidateQueries({ queryKey: key }); }} />}

      {data.records.map((r) => {
        const open = shown(r);
        const bySection = Array.from(new Set(r.checklist.map((c) => c.section)));
        return (
          <div key={r.id} className="overflow-hidden rounded-lg border border-slate-300 bg-white">
            <button type="button" onClick={() => setOpenId(open ? '' : r.id)} className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 bg-slate-50 px-3 py-2 text-left hover:bg-slate-100">
              <span className="font-bold text-[#1b3a57]">{r.stage}</span>
              <span className="text-slate-600">{fmt(r.date)}{r.time ? ` ${r.time}` : ''}</span>
              {r.inspector && <span className="text-slate-600">by {r.inspector}</span>}
              {r.result && <span className={`rounded border px-1.5 py-0.5 font-semibold ${TONE[r.resultTone]}`}>{r.result}</span>}
              {r.issues.length > 0 && <span className="text-amber-700">{r.issues.length} to attend</span>}
              <span className="ml-auto text-slate-400">{r.sourceLabel} · {open ? 'Hide ▾' : 'Show ▸'}</span>
            </button>
            {open && (
              <div className="space-y-3 p-3">
                {/* The fact sheet grid */}
                <table className="w-full border-collapse text-[12px]">
                  <tbody>
                    <tr>
                      <th className="w-32 border border-slate-300 bg-slate-100 px-2 py-1 text-left text-[10px] font-bold uppercase text-slate-500">Inspection date</th>
                      <td className="border border-slate-300 px-2 py-1 font-semibold">{fmt(r.date)}{r.time ? ` · ${r.time}` : ''}</td>
                      <th className="w-32 border border-slate-300 bg-slate-100 px-2 py-1 text-left text-[10px] font-bold uppercase text-slate-500">Inspected by</th>
                      <td className="border border-slate-300 px-2 py-1 font-semibold">{r.inspector || '—'}</td>
                    </tr>
                    <tr>
                      <th className="border border-slate-300 bg-slate-100 px-2 py-1 text-left text-[10px] font-bold uppercase text-slate-500">Where</th>
                      <td className="border border-slate-300 px-2 py-1">{r.location || '—'}</td>
                      <th className="border border-slate-300 bg-slate-100 px-2 py-1 text-left text-[10px] font-bold uppercase text-slate-500">Result</th>
                      <td className="border border-slate-300 px-2 py-1">{r.result ? <span className={`rounded border px-1.5 py-0.5 font-semibold ${TONE[r.resultTone]}`}>{r.result}</span> : '—'}</td>
                    </tr>
                    <tr>
                      <th className="border border-slate-300 bg-slate-100 px-2 py-1 text-left text-[10px] font-bold uppercase text-slate-500">Inspection report</th>
                      <td className="border border-slate-300 px-2 py-1">{r.report ? fileBtn(r.report, 'View report') : <span className="text-slate-400">Not on file</span>}</td>
                      <th className="border border-slate-300 bg-slate-100 px-2 py-1 text-left text-[10px] font-bold uppercase text-slate-500">Certificate</th>
                      <td className="border border-slate-300 px-2 py-1">
                        {r.certificate ? <span className="inline-flex flex-wrap items-center gap-1.5">{fileBtn(r.certificate, 'View certificate')}{r.certificate.validUntil && <span className="text-slate-600">valid till {fmt(r.certificate.validUntil)}</span>}</span> : <span className="text-slate-400">Not on file</span>}
                      </td>
                    </tr>
                    <tr>
                      <th className="border border-slate-300 bg-slate-100 px-2 py-1 text-left text-[10px] font-bold uppercase text-slate-500">Supporting documents</th>
                      <td className="border border-slate-300 px-2 py-1" colSpan={3}>
                        {r.supportingDocs.length ? <span className="flex flex-wrap gap-1">{r.supportingDocs.map((f) => fileBtn(f))}</span> : <span className="text-slate-400">None</span>}
                      </td>
                    </tr>
                    {(r.remarks || r.note) && (
                      <tr>
                        <th className="border border-slate-300 bg-slate-100 px-2 py-1 text-left text-[10px] font-bold uppercase text-slate-500">Remarks</th>
                        <td className="border border-slate-300 px-2 py-1" colSpan={3}>{[r.remarks, r.note].filter(Boolean).join(' · ')}</td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {r.readings.length > 0 && (
                  <div>
                    <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">Readings & details</div>
                    <div className="grid gap-x-4 gap-y-0.5 sm:grid-cols-2 lg:grid-cols-3">
                      {r.readings.map((x, i) => <div key={i} className="flex justify-between gap-2 border-b border-slate-100 py-0.5"><span className="text-slate-500">{x.label}</span><span className="font-semibold text-slate-800">{x.value}</span></div>)}
                    </div>
                  </div>
                )}

                {r.checklist.length > 0 && (
                  <div>
                    <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">Inspection checklist</div>
                    <table className="w-full border-collapse text-[12px]">
                      <tbody>
                        {bySection.map((sec) => (
                          <Fragment key={sec}>
                            <tr><td colSpan={3} className="border border-slate-300 bg-slate-100 px-2 py-0.5 text-[10.5px] font-bold uppercase text-slate-600">{sec}</td></tr>
                            {r.checklist.filter((c) => c.section === sec).map((c) => (
                              <tr key={c.key}>
                                <td className="border border-slate-200 px-2 py-0.5">{c.label}</td>
                                <td className={`w-32 whitespace-nowrap border border-slate-200 px-2 py-0.5 ${STATUS[c.status].cls}`}>
                                  {c.docStatus
                                    ? <span className={`rounded border px-1 py-px text-[10.5px] font-semibold ${DOC_BADGE[c.docStatus] || ''}`}>{c.status === 'pending' ? 'Not received' : c.docStatus === 'no_date' ? 'No validity date' : c.validUntil ? `Valid till ${fmt(c.validUntil)}` : STATUS[c.status].text}</span>
                                    : STATUS[c.status].text}
                                </td>
                                <td className="border border-slate-200 px-2 py-0.5 text-slate-600">
                                  {c.remark}
                                  {(c.files || []).length > 0 && <span className="ml-1 inline-flex gap-1">{(c.files || []).map((f, i) => fileBtn(f, (c.files || []).length > 1 ? `File ${i + 1}` : 'View'))}</span>}
                                </td>
                              </tr>
                            ))}
                          </Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {r.photos.length > 0 && (
                  <div>
                    <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">Photographs ({r.photos.length})</div>
                    <div className="flex flex-wrap gap-2">
                      {r.photos.map((p, i) => {
                        const t = media(p, 'thumb'); const full = media(p);
                        return t && full ? <Thumb key={i} m={t} onClick={() => setView({ ...full, kind: 'image' })} /> : null;
                      })}
                    </div>
                  </div>
                )}

                {r.signOffs.length > 0 && (
                  <div className="text-slate-600">
                    <span className="font-bold text-slate-500">Sign-offs: </span>
                    {r.signOffs.map((x, i) => <span key={i} className="mr-3">{/^approved$/i.test(x.status) ? <span className="font-semibold text-emerald-700">✓</span> : /^rejected$/i.test(x.status) ? <span className="font-semibold text-rose-700">✗</span> : <span className="text-amber-700">…</span>} {x.name}{x.at ? ` ${fmt(x.at)}` : ''}{x.comment ? ` — “${x.comment}”` : ''}</span>)}
                  </div>
                )}

                {r.manualId && canFile && (
                  <button type="button" onClick={async () => {
                    if (!window.confirm('Remove this inspection from the container?')) return;
                    try { await api.del(`${base}/inspection/${r.manualId}`); qc.invalidateQueries({ queryKey: key }); } catch (e: any) { toast.error(e?.message || 'Could not remove'); }
                  }} className="rounded border border-rose-200 px-2 py-0.5 text-rose-600 hover:bg-rose-50">Remove this inspection</button>
                )}
              </div>
            )}
          </div>
        );
      })}
      <div className="text-[11px] text-slate-400">Surveyor inspections, tank document checklists, gate-in checks and pre-trip inspections — read from where they are recorded. Purchase and tax papers are not shown here.</div>
      {view && <Viewer m={view} onClose={() => setView(null)} />}
    </div>
  );
}

/* ───────────────────────── filing an inspection ───────────────────────── */

interface Up { fileId: string; name: string; label: string }

function FilingForm({ containerNo, base, view, onDone }: { containerNo: string; base: string; view: View; onDone: () => void }) {
  const { user } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ inspectedOn: today, inspectedBy: String((user as any)?.name || ''), result: '', remarks: '', certificateValidUntil: '' });
  const [marks, setMarks] = useState<Record<string, { status: 'ok' | 'issue' | 'na' | ''; remark: string }>>({});
  const [report, setReport] = useState<Up | null>(null);
  const [cert, setCert] = useState<Up | null>(null);
  const [docs, setDocs] = useState<Up[]>([]);
  const [photos, setPhotos] = useState<Up[]>([]);
  const [busy, setBusy] = useState(false);

  async function up(file: File, label = ''): Promise<Up> {
    const r = await api.upload<{ id: string; filename: string }>('/api/files/upload', file, { docType: 'container-inspection' });
    return { fileId: r.id, name: r.filename || file.name, label: label || file.name };
  }
  async function pick(files: FileList | null, add: (u: Up[]) => void) {
    if (!files?.length) return;
    setBusy(true);
    try { add(await Promise.all(Array.from(files).map((x) => up(x)))); } catch (e: any) { toast.error(e?.message || 'Upload failed'); } finally { setBusy(false); }
  }
  async function save() {
    if (!f.inspectedBy.trim()) { toast.error('Say who inspected.'); return; }
    setBusy(true);
    try {
      await api.post(`${base}/inspection`, {
        inspectedOn: f.inspectedOn, inspectedBy: f.inspectedBy, result: f.result, remarks: f.remarks,
        report, certificate: cert, certificateValidUntil: f.certificateValidUntil,
        checklist: Object.entries(marks).filter(([, m]) => m.status).map(([k, m]) => ({ key: k, status: m.status, remark: m.remark })),
        supportingDocs: docs, photos,
      });
      toast.success('Inspection filed');
      onDone();
    } catch (e: any) { toast.error(e?.message || 'Could not file the inspection'); } finally { setBusy(false); }
  }
  const mark = (k: string, status: 'ok' | 'issue' | 'na') => setMarks((m) => ({ ...m, [k]: { status: m[k]?.status === status ? '' : status, remark: m[k]?.remark || '' } }));
  const list = (items: Up[], set: (u: Up[]) => void) => items.map((u, i) => <span key={i} className="mr-1 inline-flex items-center gap-1 rounded bg-white px-1.5 py-0.5 text-[11px] text-slate-700">{u.name}<button type="button" className="text-rose-600" onClick={() => set(items.filter((_, j) => j !== i))}>×</button></span>);

  return (
    <div className="space-y-3 rounded-lg border border-indigo-200 bg-indigo-50/40 p-3">
      <div className="font-bold text-[#1b3a57]">{view.template.title} — {containerNo}</div>
      <div className="grid gap-2 sm:grid-cols-4">
        <label>Inspection date<input type="date" className="input mt-0.5 w-full" value={f.inspectedOn} max={today} onChange={(e) => setF({ ...f, inspectedOn: e.target.value })} /></label>
        <label className="sm:col-span-2">Inspected by<input className="input mt-0.5 w-full" value={f.inspectedBy} maxLength={120} onChange={(e) => setF({ ...f, inspectedBy: e.target.value })} placeholder="Name of the person or agency" /></label>
        <label>Result
          <select className="input mt-0.5 w-full" value={f.result} onChange={(e) => setF({ ...f, result: e.target.value })}>
            <option value="">—</option><option value="pass">Passed</option><option value="pass_with_repairs">Passed with repairs</option><option value="fail">Failed</option>
          </select>
        </label>
      </div>

      <div>
        <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">Inspection checklist — {view.assetLabel.toLowerCase()}</div>
        {view.template.sections.map((sec) => (
          <div key={sec.title} className="mb-2">
            <div className="bg-slate-100 px-2 py-0.5 text-[10.5px] font-bold uppercase text-slate-600">{sec.title}</div>
            {sec.items.map((it) => {
              const m = marks[it.key];
              return (
                <div key={it.key} className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-2 py-0.5">
                  <span className="min-w-[200px] flex-1">{it.label}</span>
                  {(['ok', 'issue', 'na'] as const).map((st) => (
                    <button key={st} type="button" onClick={() => mark(it.key, st)} className={`rounded border px-1.5 py-px text-[11px] font-semibold ${m?.status === st ? (st === 'issue' ? 'border-rose-400 bg-rose-100 text-rose-800' : st === 'ok' ? 'border-emerald-400 bg-emerald-100 text-emerald-800' : 'border-slate-400 bg-slate-200 text-slate-700') : 'border-slate-200 bg-white text-slate-500'}`}>{st === 'ok' ? 'OK' : st === 'issue' ? 'Issue' : 'N/A'}</button>
                  ))}
                  {m?.status === 'issue' && <input className="input w-56" placeholder="What is wrong" value={m.remark} maxLength={500} onChange={(e) => setMarks({ ...marks, [it.key]: { ...m, remark: e.target.value } })} />}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        <label>Inspection report<input type="file" className="mt-0.5 block w-full" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={async (e) => { const x = e.target.files?.[0]; if (!x) return; setBusy(true); try { setReport(await up(x, 'Inspection report')); } catch (er: any) { toast.error(er?.message || 'Upload failed'); } finally { setBusy(false); } }} />{report && <span className="text-[11px] text-emerald-700">✓ {report.name}</span>}</label>
        <label>Inspection certificate<input type="file" className="mt-0.5 block w-full" accept=".pdf,.jpg,.jpeg,.png" onChange={async (e) => { const x = e.target.files?.[0]; if (!x) return; setBusy(true); try { setCert(await up(x, 'Inspection certificate')); } catch (er: any) { toast.error(er?.message || 'Upload failed'); } finally { setBusy(false); } }} />{cert && <span className="text-[11px] text-emerald-700">✓ {cert.name}</span>}</label>
        <label>Certificate valid until<input type="date" className="input mt-0.5 w-full" value={f.certificateValidUntil} onChange={(e) => setF({ ...f, certificateValidUntil: e.target.value })} /></label>
        <label>Supporting documents<input type="file" multiple className="mt-0.5 block w-full" onChange={(e) => pick(e.target.files, (u) => setDocs((d) => [...d, ...u]))} /><div>{list(docs, setDocs)}</div></label>
        <label className="sm:col-span-2">Photographs<input type="file" multiple accept="image/*" className="mt-0.5 block w-full" onChange={(e) => pick(e.target.files, (u) => setPhotos((d) => [...d, ...u]))} /><div>{list(photos, setPhotos)}</div></label>
        <label className="sm:col-span-2">Remarks<textarea className="input mt-0.5 w-full" rows={2} maxLength={3000} value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} /></label>
      </div>
      <div className="flex justify-end"><button type="button" disabled={busy} onClick={save} className="rounded-md bg-indigo-600 px-4 py-1.5 font-semibold text-white disabled:opacity-60">{busy ? 'Please wait…' : 'Save inspection'}</button></div>
    </div>
  );
}
